/**
 * Pure, framework-free install-prompt state.
 *
 * Everything here is device-local by construction: the values live in this
 * browser's localStorage/sessionStorage, so installing StockWise on one phone
 * never changes what another employee sees on theirs, and a shop account's
 * preference is never synced to a second device.
 *
 * The functions take their storage and clock as arguments so they can be
 * tested without a DOM.
 */

/** How long after login the install notification may first appear. */
export const DEFAULT_PROMPT_DELAY_MS = 20_000

/** Cooldown applied when the user picks "Maybe later". */
export const DEFAULT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000

/** After this many cooldown dismissals the device opts out for good. */
export const MAX_DISMISSALS = 3

export const STORAGE_KEYS = {
  installed: 'stockwise:pwa:installed',
  optedOut: 'stockwise:pwa:opt-out',
  cooldownUntil: 'stockwise:pwa:cooldown-until',
  dismissCount: 'stockwise:pwa:dismiss-count',
  sessionDismissed: 'stockwise:pwa:session-dismissed',
} as const

/**
 * How this browser can be asked to install StockWise:
 * - `native`       Chromium fires `beforeinstallprompt` → real system dialog
 * - `browser-menu` desktop Chrome/Edge without the event → guide to the menu
 * - `ios-guide`    iOS Safari has no install API → Share → Add to Home Screen
 * - `none`         nothing sensible to offer (Firefox, already installed, …)
 */
export type InstallCapability = 'native' | 'browser-menu' | 'ios-guide' | 'none'

/** Minimal storage surface — keeps the functions testable with a plain map. */
export interface KeyValueStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export interface TimingConfig {
  promptDelayMs: number
  cooldownMs: number
  maxDismissals: number
}

function parseMs(raw: string | undefined, fallback: number): number {
  const value = Number(raw)
  return Number.isFinite(value) && value >= 0 ? value : fallback
}

/** Delay/cooldown are configurable at build time (see .env / Vite env vars). */
export function getTimingConfig(
  env: Record<string, string | undefined> = import.meta.env as unknown as Record<string, string | undefined>,
): TimingConfig {
  return {
    promptDelayMs: parseMs(env.VITE_PWA_INSTALL_DELAY_MS, DEFAULT_PROMPT_DELAY_MS),
    cooldownMs: parseMs(env.VITE_PWA_INSTALL_COOLDOWN_MS, DEFAULT_COOLDOWN_MS),
    maxDismissals: MAX_DISMISSALS,
  }
}

export interface InstallEnvironment {
  userAgent?: string
  standalone?: boolean
}

/** True when StockWise is running as an installed app rather than a tab. */
export function isStandalone(env: InstallEnvironment = {}): boolean {
  if (typeof env.standalone === 'boolean') return env.standalone
  if (typeof window === 'undefined') return false
  const mq = window.matchMedia?.('(display-mode: standalone)')
  if (mq?.matches) return true
  // Legacy iOS Safari flag on window, and any app-scheme referrer.
  if ((window as unknown as { navigator?: { standalone?: boolean } }).navigator?.standalone) return true
  return document.referrer.startsWith('app://')
}

/** iOS/iPadOS — every browser there is WebKit, so the install path is fixed. */
export function isIOS(userAgent: string | undefined = typeof navigator === 'undefined' ? undefined : navigator.userAgent): boolean {
  if (!userAgent) return false
  return /iPad|iPhone|iPod/.test(userAgent) || (userAgent.includes('Mac') && navigator.maxTouchPoints > 1)
}

/**
 * Desktop Chromium without `beforeinstallprompt` still has a documented
 * install path in its own menu — we only offer that guidance, never a fake
 * native dialog.
 */
export function isDesktopChromium(userAgent: string | undefined = typeof navigator === 'undefined' ? undefined : navigator.userAgent): boolean {
  if (!userAgent) return false
  const chromium = /Chrome|Chromium|Edg|OPR|Brave/i.test(userAgent)
  const mobile = /Android|Mobile|iPad|iPod/i.test(userAgent)
  return chromium && !mobile && !isIOS(userAgent)
}

export function isAndroidChromium(userAgent: string | undefined = typeof navigator === 'undefined' ? undefined : navigator.userAgent): boolean {
  if (!userAgent) return false
  return /Android/i.test(userAgent) && /Chrome|Chromium|Edg|Samsung/i.test(userAgent)
}

/** Decide which installation story, if any, this browser should be shown. */
export function detectCapability(env: InstallEnvironment = {}, nativePromptAvailable = false): InstallCapability {
  if (isStandalone(env)) return 'none'
  if (isIOS(env.userAgent)) return 'ios-guide'
  if (nativePromptAvailable) return 'native'
  if (isAndroidChromium(env.userAgent)) return 'native' // waits for the event
  if (isDesktopChromium(env.userAgent)) return 'browser-menu'
  return 'none'
}

/** A device that already told us the app got installed never hears about it again. */
export function hasInstalled(storage: KeyValueStore): boolean {
  return storage.getItem(STORAGE_KEYS.installed) === '1'
}

export interface DismissalState {
  /** True while the notification must stay hidden. */
  blocked: boolean
  reason: 'installed' | 'opted-out' | 'cooldown' | 'session' | null
}

/**
 * Read every reason the prompt must stay hidden right now.
 * Order matters: permanent states are checked before temporary ones so the
 * reported reason always matches the strongest rule.
 */
export function readDismissalState(
  storage: KeyValueStore,
  session: KeyValueStore,
  now: number,
): DismissalState {
  if (hasInstalled(storage)) return { blocked: true, reason: 'installed' }
  if (storage.getItem(STORAGE_KEYS.optedOut) === '1') return { blocked: true, reason: 'opted-out' }

  const until = Number(storage.getItem(STORAGE_KEYS.cooldownUntil) ?? 0)
  if (Number.isFinite(until) && until > now) return { blocked: true, reason: 'cooldown' }

  if (session.getItem(STORAGE_KEYS.sessionDismissed) === '1') return { blocked: true, reason: 'session' }

  return { blocked: false, reason: null }
}

/** "Dismiss" — quiet for the rest of this session only. */
export function recordSessionDismissal(session: KeyValueStore): void {
  session.setItem(STORAGE_KEYS.sessionDismissed, '1')
}

/**
 * "Maybe later" — a configurable cooldown on this device, escalating to a
 * permanent opt-out once the user has pushed it away `maxDismissals` times.
 * Returns true when the permanent opt-out was just set.
 */
export function recordCooldownDismissal(
  storage: KeyValueStore,
  now: number,
  config: TimingConfig,
): boolean {
  storage.setItem(STORAGE_KEYS.cooldownUntil, String(now + config.cooldownMs))

  const count = Number(storage.getItem(STORAGE_KEYS.dismissCount) ?? '0') + 1
  storage.setItem(STORAGE_KEYS.dismissCount, String(count))

  if (count >= config.maxDismissals) {
    storage.setItem(STORAGE_KEYS.optedOut, '1')
    return true
  }
  return false
}

/** The browser confirmed installation (`appinstalled` or display-mode change). */
export function recordInstalled(storage: KeyValueStore): void {
  storage.setItem(STORAGE_KEYS.installed, '1')
  storage.removeItem(STORAGE_KEYS.cooldownUntil)
  storage.removeItem(STORAGE_KEYS.sessionDismissed)
}

/**
 * Routes where an install/update card must never appear: it would cover the
 * checkout, the POS sale button or the payment form.
 */
export function isInterruptiblePath(pathname: string): boolean {
  const blocked = [
    '/store/', // public storefront + checkout
    '/production/quick-sale', // POS sale screen
    '/pay', // subscription payment
    '/production/run',
  ]
  return !blocked.some((prefix) => pathname === prefix || pathname.startsWith(prefix))
}
